import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { first, get } from 'lodash';
import { AuthResult } from 'node_modules/angular-auth-oidc-client/lib/flows/callback-context';
import {
  filter,
  interval,
  take, takeUntil,
  tap
} from 'rxjs';
import { AbstractComponent } from '../shared/components/abstract-component';
import { IAppConfig } from '../shared/interfaces/app-config';
import { User } from '../shared/models/user';
import { AuthStore } from '../stores/auth.store';
import { UserService } from './user.service';

/**
 * Token monitoring interval in milliseconds (2 minutes)
 */
 
const TOKEN_MONITOR_INTERVAL_MS: number = 2 * 60 * 1000;

/**
 * Token refresh threshold in milliseconds (5 minutes)
 */
 
const TOKEN_REFRESH_THRESHOLD_MS: number = 5 * 60 * 1000;

/**
 * OidcAuthResult interface for authentication response
 */
interface OidcAuthResult {
  isAuthenticated: boolean;
  userData: Record<string, unknown>;
  accessToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AbstractComponent {
  config: IAppConfig;

  constructor(
    private userService: UserService,
    private oidcSecurityService: OidcSecurityService,
    private authStore: AuthStore,
  ) {
    super();
    this.monitorToken();
  }

  monitorToken(): void {
    interval(TOKEN_MONITOR_INTERVAL_MS)
      .pipe(
        tap(() => {
          this.checkTokenExpiry();
        })
      )
      .subscribe();
  }

  private checkTokenExpiry() {
    const authResult: AuthResult = this.authStore?.AuthResult;
    if (authResult) {
      const timeRemaining: number = authResult.expires_in - Date.now();

      // If token expires in less than 5 minutes, refresh it
      if (timeRemaining < TOKEN_REFRESH_THRESHOLD_MS) {
        this.initAuth();
      };
    } else {
      // Initial token fetch if authResult is null
      return this.initAuth();
    }
  }

  initAuth(): void {
    this.oidcSecurityService.getAuthenticationResult().pipe(takeUntil(this.componentDestroyed))
      .subscribe((authResult: AuthResult) => {
        this.authStore.initializeCredentials(authResult)
      });
  }

  startAuthentication(): void {
    this.oidcSecurityService.authorize();
  }

  logout() : void{
    this.oidcSecurityService
      .logoff()
      .subscribe(() => { /* logout complete */ });
  }

  completeAuthentication(): void {
    this.oidcSecurityService.checkAuth().pipe(takeUntil(this.componentDestroyed)).subscribe({
      next: ({ isAuthenticated, userData }: OidcAuthResult) => {
        if (isAuthenticated) {

          this.oidcSecurityService.getAuthenticationResult().pipe(takeUntil(this.componentDestroyed))
            .subscribe((authResult: AuthResult) => {
              this.authStore.initializeCredentials(authResult);
              this.userService.getUserInfo().pipe(
                filter((userDetails: User | null) => !!userDetails && !!authResult),
                 
                take(1),
                takeUntil(this.componentDestroyed))
                .subscribe((userDetails: User) => {
                  this.authStore.initializeLogInUser(new User({
                    ...userData,
                    ...userDetails,
                    selectedRole: first(userDetails?.userRoles),
                    selectedLocation: first(get(first(userDetails?.userRoles), 'locations')),
                    firstName: userDetails?.firstName ?? (userData.profile as Record<string, unknown>)?.given_name as string,
                    lastName: userDetails?.lastName ?? (userData.profile as Record<string, unknown>)?.family_name as string,
                    email: (userDetails as any)?.EmailAddress ?? (userData.profile as Record<string, unknown>)?.EmailAddress as string
                  }));
                });
            });
        } else {
          this.oidcSecurityService.authorize();
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      error: (_error: unknown) => {
        this.oidcSecurityService.authorize();
      }
    });
  }

  override afterOnDestroy(): void {
    // afterOnDestroy
  }
}
